import { CallAPI } from "../../apiManager/ApiCall";
import { revenueCatSubscriptionsURL } from "../../apiManager/APIConfig";

/**
 * Fetch RevenueCat subscriptions with pagination support
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Items per page (default: 50)
 * @param {number} params.offset - Starting position (default: 0)
 * @param {string} params.status - Filter by status (optional): SUBSCRIBED, RENEWED, UNSUBSCRIBED
 * @param {string} params.user_id - Filter by specific user ID (optional)
 * @param {number} params.concurrency - API concurrency (default: 10)
 * @param {string} params.search - Search by name or email (optional)
 */
export const GetSubscriptionsCall = async (params = {}) => {
  try {
    const {
      limit = 50,
      offset = 0,
      status = null,
      user_id = null,
      concurrency = 10,
      include_raw = "0",
      search = "",
    } = params;

    // Build query string
    const queryParams = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      concurrency: concurrency.toString(),
      include_raw,
    });

    if (status) queryParams.append("status", status);
    if (user_id) queryParams.append("user_id", user_id);
    if (search && String(search).trim()) {
      queryParams.append("search", String(search).trim());
    }

    const response = await CallAPI({
      url: `${revenueCatSubscriptionsURL}?${queryParams.toString()}`,
      method: "GET",
    });

    // Handle error response
    if (response?.success === false) {
      throw new Error(response?.message || "Failed to fetch subscriptions");
    }

    // Return the full response with pagination metadata
    return {
      data: response?.data || [],
      total: response?.total || 0,
      total_users: response?.total_users || 0,
      tab_totals: response?.tab_totals || {},
      scanned: response?.scanned || 0,
      pagination: response?.pagination || {},
      performance: response?.performance || {},
      warnings: response?.warnings || [],
    };
  } catch (error) {
    throw new Error(error?.message || "Failed to fetch subscriptions");
  }
};

/**
 * Fetch ALL subscriptions across multiple pages
 * (Use with caution - can be slow for large datasets)
 */
export const GetAllSubscriptionsCall = async (params = {}) => {
  try {
    let allData = [];
    let offset = 0;
    const limit = params.limit || 50;
    let hasMore = true;

    while (hasMore) {
      const response = await GetSubscriptionsCall({
        ...params,
        limit,
        offset,
      });

      allData = [...allData, ...response.data];

      hasMore = response.pagination?.has_more || false;
      offset = response.pagination?.next_offset || offset + limit;

      // Safety check to prevent infinite loops
      if (offset > 10000) {
        console.warn("⚠️ Reached maximum offset of 10,000. Stopping fetch.");
        break;
      }
    }

    return allData;
  } catch (error) {
    throw new Error(error?.message || "Failed to fetch all subscriptions");
  }
};
